import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import clsx from "clsx";
import { Grid, Stack, Button } from "@mui/material";

import OemDataTable from "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable";
import SectionContainer from "@Layouts/components/SectionContainer";
import CustomizedSwitches from "@Layouts/components/InputComponents/CustomizedSwitches";

// Constants
import { iconsMap } from "@Constants/iconsMap";

// Styles
import "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable.css";

import closeExpand from "@/assets/icons/dataPages/oem/close white.svg";
import openExpand from "@/assets/icons/dataPages/oem/open white.svg";

export default function OemDataSections(props) {
	const [sections, setSections] = useState([]);
	const [sectionOpened, setSectionOpened] = useState(null);
	const [topScrollValue, setTopScrollValue] = useState(null);
	const [regionSwitch, setRegionSwitch] = useState(false);
	const location = useSelector((state) => state.location.location);

	const onChangeTopScroll = (value) => {
		setTopScrollValue(value);
	};

	useEffect(() => {
		setSections(props?.content);

		return () => {
			setSections([]);
		};
	}, [props]);

	const getSection = (section) => {
		const parentsProps = { ...props.parentsProps, parents: [...props.parentsProps.parents, section._id] };
		switch (section.sectionType) {
			case "SECTION":
				return (
					<Grid container m={0}>
						<OemDataSections
							content={section.content}
							parentsProps={parentsProps}
							onUpdateCellTableValue={props?.onUpdateCellTableValue}
							filterRankingDataPage={props.filterRankingDataPage}
							tableProps={props?.tableProps}
							oemDataTab={props.oemDataTab}
						/>
					</Grid>
				);
			case "TABLE": {
				const shouldHaveCustomizedSwitches = !!section?.properties?.regionSwitch && props.filterRankingDataPage;

				const filteredData = () => {
					if (shouldHaveCustomizedSwitches && props.filterRankingDataPage) {
						const result = props.filterRankingDataPage({
							content: section.content,
							tableInformation: section.tableInformation,
							dealerCodeFS: props.parentsProps?.dealerCodeFS,
							franchise: props.parentsProps?.franchise,
							locationObj: location,
							applyFilter: regionSwitch,
						});
						return {
							...section,
							content: result,
						};
					} else {
						return section;
					}
				};

				const topScrollProps =
					section.properties?.isScrollParent || section.properties?.isScrollChild
						? { topScrollValue, changeTopScroll: onChangeTopScroll }
						: {};

				return (
					<Grid container m={0} paddingX={3} style={section.styles}>
						{shouldHaveCustomizedSwitches && (
							<CustomizedSwitches
								sx={{ mb: 5 }}
								textRight
								textChecked={section.properties.regionSwitch}
								textUnChecked={section.properties.regionSwitch}
								checked={regionSwitch}
								onChange={(value) => setRegionSwitch(value)}
								tableProps={props?.tableProps}
							/>
						)}
						<OemDataTable
							{...topScrollProps}
							content={filteredData()}
							parentsProps={parentsProps}
							onUpdateCellTableValue={props?.onUpdateCellTableValue}
							tableProps={props?.tableProps}
						/>
					</Grid>
				);
			}
			case "SEPARATOR":
				return (
					<Grid container m={0} paddingX={2}>
						<Stack
							sx={{
								width: "100%",
								height: section.height,
							}}
						/>
					</Grid>
				);
			default:
				return <></>;
		}
	};

	const getSectionTitle = (section) => {
		return Boolean(section.sectionTitle || section.lineNumber) ? (
			<Grid
				container
				className="header-section"
				sx={{
					paddingX: 3,
				}}
			>
				<Grid
					item
					className="header-section-line-number"
					sx={{
						width: section.lineNumberWidth,
					}}
				>
					{section.lineNumber}
				</Grid>
				<Grid item className="header-section-title" style={section.styles}>
					<Grid item>{props.oemDataTab == "CSI" ? props.parentsProps?.franchise : ""} {section.sectionTitle}</Grid>
				</Grid>
			</Grid>
		) : (
			<></>
		);
	};

	const brandIcon = () => {
		if (props.oemDataTab !== "CSI" || !props.parentsProps?.franchise || props.parentsProps?.franchise.split("/").length > 1)  {
			return null;
		}

		const iconIndex = props.parentsProps?.franchise.split("/")[0].toLowerCase();
		if (!iconsMap[iconIndex])  {
			return null;
		}

		return {
			"iconName": iconIndex,
			"styles": {
				"width": 150,
				"marginRight": 10
			}
		};
	}

	const getSectionLayer = (key, section, space) => {
		return (
			<Grid key={`${key}_${section._id}`} item xs={space}>
				{Boolean(section.maxSpace) && (
					<Stack style={{ position: "relative" }}>
						<Button className={clsx({ "floating-expand-section-btn": true })} onClick={() => onSectionExpand(section)}>
							<img src={section._id === sectionOpened ? closeExpand : openExpand} alt="icon" />
						</Button>
					</Stack>
				)}
				{Boolean(section.collapsible) ? (
					<SectionContainer
						sectionId={section._id}
						title={(props.oemDataTab === "CSI" ? props.parentsProps?.franchise + " " : "") + section.sectionTitle}
						collapsible
						leftAlignment={props.oemDataTab === "CSI"}
						sx={{ 
							boxShadow: "none !important", 
							padding: (props.oemDataTab === "CSI" ? "10px 20px !important" : "10px 30px !important")
						}}
						brandIcon={brandIcon()}
					>
						<Stack py={2}>{getSection(section)}</Stack>
					</SectionContainer>
				) : (
					<>
						{getSectionTitle(section)}
						{getSection(section)}
					</>
				)}
			</Grid>
		);
	};

	const onSectionExpand = (section) => {
		if (Boolean(section._id === sectionOpened)) {
			setSectionOpened(null);
		} else {
			setSectionOpened(section._id);
		}
	};

	return (
		<>
			{sections?.map((section, key) => {
				const space = Boolean(sectionOpened)
					? section._id === sectionOpened
						? section.maxSpace
						: section.minSpace
					: section.space;

				return getSectionLayer(key, section, space);
			})}
		</>
	);
}

OemDataSections.propTypes = {
	tableProps: PropTypes.object,
	content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	parentsProps: PropTypes.object,
	onUpdateCellTableValue: PropTypes.func,
	filterRankingDataPage: PropTypes.func,
	oemDataTab: PropTypes.string,
};
